import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  HelpCircle,
  Loader2,
} from 'lucide-react';
import React, { useState } from 'react';

import { GenericPdfCitationViewer } from '@/components/citations/GenericPdfCitationViewer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useGetAgentChatCitationQuery } from '@/services/api/agentChatService';
import {
  useGetProofreadingResultsQuery,
  useGetWorkflowStatusQuery,
} from '@/services/api/demandReviewService';
import { TMaterializedCitationBlock } from '@/services/types/agent-chat-types';
import {
  ProofreadingJob,
  ProofreadingJobStatus,
  ProofreadingRuleResult,
  ProofreadingRuleResultStatus,
  WorkflowStatusResponse,
} from '@/services/types/demand-review-types';

import type { DemandCitationHighlight } from './DemandPdfViewer';

interface ProofreadingResultsProps {
  matterId: string;
  workspaceId: string;
  demandArtifactId?: string | null;
  /** Callback when a demand citation is clicked - receives citation blocks to highlight */
  onDemandCitationClick?: (citation: DemandCitationHighlight) => void;
}

function getStatusIcon(status: ProofreadingRuleResultStatus) {
  switch (status) {
    case ProofreadingRuleResultStatus.NO_ISSUES_FOUND:
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case ProofreadingRuleResultStatus.CRITICAL_ISSUES_FOUND:
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    case ProofreadingRuleResultStatus.UNABLE_TO_ASSESS:
      return <HelpCircle className="h-5 w-5 text-yellow-500" />;
    case ProofreadingRuleResultStatus.PROCESSING:
    default:
      return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  }
}

function getStatusBadgeVariant(
  status: ProofreadingRuleResultStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case ProofreadingRuleResultStatus.NO_ISSUES_FOUND:
      return 'default';
    case ProofreadingRuleResultStatus.CRITICAL_ISSUES_FOUND:
      return 'destructive';
    case ProofreadingRuleResultStatus.UNABLE_TO_ASSESS:
      return 'secondary';
    case ProofreadingRuleResultStatus.PROCESSING:
    default:
      return 'outline';
  }
}

function getStatusLabel(status: ProofreadingRuleResultStatus): string {
  switch (status) {
    case ProofreadingRuleResultStatus.NO_ISSUES_FOUND:
      return 'No Issues';
    case ProofreadingRuleResultStatus.CRITICAL_ISSUES_FOUND:
      return 'Issues Found';
    case ProofreadingRuleResultStatus.UNABLE_TO_ASSESS:
      return 'Unable to Assess';
    case ProofreadingRuleResultStatus.PROCESSING:
    default:
      return 'Processing';
  }
}

function getJobStatusBadgeVariant(
  status: ProofreadingJobStatus,
): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case ProofreadingJobStatus.COMPLETED:
      return 'default';
    case ProofreadingJobStatus.FAILED:
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * CitationButton - renders differently based on whether citation is from demand or external file
 */
interface CitationButtonProps {
  matterId: string;
  citationId: string;
  demandArtifactId?: string | null;
  onDemandCitationClick?: (citation: DemandCitationHighlight) => void;
  index: number;
}

const CitationButton = ({
  matterId,
  citationId,
  demandArtifactId,
  onDemandCitationClick,
  index,
}: CitationButtonProps) => {
  // Fetch citation to check artifact_id
  const { data: citation, isLoading } = useGetAgentChatCitationQuery({
    matterId,
    citationId,
  });

  if (isLoading) {
    return (
      <Button size="sm" variant="ghost" disabled className="h-7 px-2 text-xs">
        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        Loading...
      </Button>
    );
  }

  if (!citation) {
    return null;
  }

  const isDemandCitation =
    demandArtifactId && citation.artifact_id === demandArtifactId;
  const firstPage = (citation.blocks?.[0]?.page_number ?? 0) + 1; // Convert to 1-indexed for display

  if (isDemandCitation && onDemandCitationClick) {
    // Demand citation - show button that highlights the cited region in the existing viewer
    return (
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        onClick={() =>
          onDemandCitationClick({
            blocks: citation.blocks as TMaterializedCitationBlock[],
          })
        }
      >
        <FileText className="mr-1 h-3 w-3" />
        Page {firstPage}
      </Button>
    );
  }

  // External citation - show modal viewer
  return (
    <GenericPdfCitationViewer
      matterId={matterId}
      citationId={citationId}
      triggerText={`Source ${index + 1}`}
      compactTrigger
      dialogTitle={citation.name || 'Source Document'}
    />
  );
};

interface RuleResultItemProps {
  matterId: string;
  result: ProofreadingRuleResult;
  demandArtifactId?: string | null;
  onDemandCitationClick?: (citation: DemandCitationHighlight) => void;
}

const RuleResultItem = ({
  matterId,
  result,
  demandArtifactId,
  onDemandCitationClick,
}: RuleResultItemProps) => {
  const [isExpanded, setIsExpanded] = useState(
    result.status === ProofreadingRuleResultStatus.CRITICAL_ISSUES_FOUND,
  );

  const hasFindings = result.findings && result.findings.length > 0;

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => hasFindings && setIsExpanded(!isExpanded)}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
          hasFindings ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'
        }`}
      >
        {!hasFindings && <span className="w-4" />}
        {hasFindings && isExpanded && (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        {hasFindings && !isExpanded && (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        {getStatusIcon(result.status)}
        <span className="flex-1 font-medium">{result.title}</span>
        <Badge variant={getStatusBadgeVariant(result.status)}>
          {getStatusLabel(result.status)}
        </Badge>
      </button>

      {isExpanded && hasFindings && (
        <div className="border-t border-border bg-muted/30 px-4 py-3">
          <div className="space-y-3">
            {result.findings.map((finding) => {
              const hasCitations =
                finding.citation_ids && finding.citation_ids.length > 0;

              return (
                <div
                  key={finding.id}
                  className="rounded-lg border border-border bg-background p-3"
                >
                  <p className="text-sm">{finding.description}</p>

                  {/* Citation buttons - different UI for demand vs external */}
                  {hasCitations && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {finding.citation_ids.map((citationId, idx) => (
                        <CitationButton
                          key={citationId}
                          matterId={matterId}
                          citationId={citationId}
                          demandArtifactId={demandArtifactId}
                          onDemandCitationClick={onDemandCitationClick}
                          index={idx}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {result.reason && (
            <p className="mt-3 text-xs text-muted-foreground">
              Reason: {result.reason}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

interface WorkflowProgressBarProps {
  workflowStatus: WorkflowStatusResponse;
}

const WorkflowProgressBar = ({ workflowStatus }: WorkflowProgressBarProps) => {
  const { proofreading } = workflowStatus;
  const progressPercentage =
    proofreading.total_rules > 0
      ? Math.round(
          (proofreading.processed_rules / proofreading.total_rules) * 100,
        )
      : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          Processing rules... {proofreading.processed_rules} /{' '}
          {proofreading.total_rules}
        </span>
        <span className="font-medium">{progressPercentage}%</span>
      </div>
      <Progress value={progressPercentage} className="h-2" />
    </div>
  );
};

interface ProofreadingJobSummaryProps {
  job: ProofreadingJob;
}

const ProofreadingJobSummary = ({ job }: ProofreadingJobSummaryProps) => {
  const issuesCount = job.results.filter(
    (r) => r.status === ProofreadingRuleResultStatus.CRITICAL_ISSUES_FOUND,
  ).length;
  const passedCount = job.results.filter(
    (r) => r.status === ProofreadingRuleResultStatus.NO_ISSUES_FOUND,
  ).length;
  const unableToAssessCount = job.results.filter(
    (r) => r.status === ProofreadingRuleResultStatus.UNABLE_TO_ASSESS,
  ).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span>{passedCount} passed</span>
        </div>
        {issuesCount > 0 && (
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span>{issuesCount} with issues</span>
          </div>
        )}
        {unableToAssessCount > 0 && (
          <div className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-yellow-500" />
            <span>{unableToAssessCount} needs review</span>
          </div>
        )}
        <span className="text-muted-foreground">
          {job.completed_at
            ? `Completed ${formatDistanceToNow(new Date(job.completed_at), {
                addSuffix: true,
              })}`
            : `Started ${formatDistanceToNow(new Date(job.created_at), {
                addSuffix: true,
              })}`}
        </span>
      </div>
    </div>
  );
};

export const ProofreadingResults = ({
  matterId,
  workspaceId,
  demandArtifactId,
  onDemandCitationClick,
}: ProofreadingResultsProps) => {
  const [stopResultsPolling, setStopResultsPolling] = useState(false);

  // Poll workflow status while proofreading is running
  const {
    data: workflowStatus,
    isLoading: workflowLoading,
    isFetching: workflowFetching,
  } = useGetWorkflowStatusQuery(
    { matterId, workspaceId },
    {
      // Poll every 2s while proofreading is running for progress updates (reduced from 500ms)
      pollingInterval: 2000,
      skipPollingIfUnfocused: true,
    },
  );

  // Determine if proofreading is actively running via the workflow
  const isProofreadingRunning = workflowStatus?.proofreading?.is_running;

  // Calculate polling interval - poll less frequently when proofreading is running
  const resultsPollingInterval = (() => {
    if (stopResultsPolling) return 0;
    if (isProofreadingRunning) return 10000;
    return 5000;
  })();

  // Fetch persisted results - poll less frequently, mainly to get final results
  const {
    data: job,
    isLoading: resultsLoading,
    error: resultsError,
  } = useGetProofreadingResultsQuery(
    { matterId, workspaceId },
    {
      pollingInterval: resultsPollingInterval,
      skipPollingIfUnfocused: true,
    },
  );

  // Stop polling once we have completed results
  React.useEffect(() => {
    if (
      job?.status === ProofreadingJobStatus.COMPLETED ||
      job?.status === ProofreadingJobStatus.FAILED
    ) {
      setStopResultsPolling(true);
    }
  }, [job?.status]);

  // Show loading state
  if (workflowLoading && resultsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Proofreading Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // If proofreading is running, show live progress from workflow
  if (isProofreadingRunning && workflowStatus) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Proofreading Results</CardTitle>
            <Badge variant="secondary">
              {workflowFetching ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : null}
              Running
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 py-4">
            {workflowStatus.proofreading.total_rules > 0 ? (
              <WorkflowProgressBar workflowStatus={workflowStatus} />
            ) : (
              <div className="flex h-20 items-center justify-center">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                <span>Starting proofreading...</span>
              </div>
            )}
            <p className="text-center text-xs text-muted-foreground">
              {workflowStatus.status_description}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no job found and not running, show waiting state
  if (resultsError || !job) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Proofreading Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-32 flex-col items-center justify-center text-center text-muted-foreground">
            <p>No proofreading results yet</p>
            <p className="text-sm">
              Results will appear here after proofreading runs
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show completed/failed job results
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Proofreading Results</CardTitle>
          <Badge variant={getJobStatusBadgeVariant(job.status)}>
            {job.status}
          </Badge>
        </div>
        {job.results.length > 0 && <ProofreadingJobSummary job={job} />}
      </CardHeader>
      <CardContent className="pt-0">
        {job.results.length === 0 && (
          <div className="py-4 text-center text-sm text-muted-foreground">
            No proofreading rules were run
          </div>
        )}
        {job.results.length > 0 && (
          <ScrollArea className="h-full max-h-[calc(100vh-350px)]">
            <div className="rounded-lg border border-border">
              {job.results.map((result) => (
                <RuleResultItem
                  key={result.id}
                  matterId={matterId}
                  result={result}
                  demandArtifactId={demandArtifactId}
                  onDemandCitationClick={onDemandCitationClick}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
